import React, { useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { Layers, Receipt } from 'lucide-react';
import { Modal } from '@components/Dialog';
import { Button } from '@components/Button';
import { DateInputBR } from '@components/DateInputBR';
import { MoneyInput } from '@components/MoneyInput';
import creditCardService from '@services/creditCardService';
import type { CardTransaction, Category, CreditCard } from '@/types/finance';
import { addCompetence, cardCompetence, competenceLabel } from '@/utils/competence';
import { todayISO } from '@/utils/formatters';
import { splitInstallmentsCents } from '@/utils/installments';
import { formatBRL } from '@/utils/money';
import { CategorySelect, MovementToggle, Required, labelCls } from './financeBits';

interface Props {
  card: CreditCard;
  /** Edição (sem parcelamento). */
  transaction?: CardTransaction | null;
  categories: Category[];
  /** Data pré-preenchida (ex.: dentro da fatura selecionada). */
  prefillDate?: string;
  onClose: () => void;
  onSaved: () => void;
  onError: (err: unknown) => void;
}

const MAX_INSTALLMENTS = 72;

const signed = (movement: 'expense' | 'income', abs: number) => (movement === 'expense' ? -Math.abs(abs) : Math.abs(abs));

/**
 * Item de fatura (POST/PUT /credit-cards/transactions): cartão travado, data da compra, descrição,
 * Compra/Estorno + valor absoluto (o sinal é aplicado aqui), categoria de despesa, parcelas (1..72,
 * prévia com as mesmas regras do backend: sobra de centavos na 1ª, uma fatura por parcela a partir
 * da competência da compra), observações e valor/moeda original opcionais.
 */
export const CardTransactionFormModal: React.FC<Props> = ({ card, transaction, categories, prefillDate, onClose, onSaved, onError }) => {
  const isEdit = !!transaction;
  const isInstallment = !!transaction?.transaction_group && !!transaction?.repeat_total && transaction.repeat_total > 1;
  const [saving, setSaving] = useState(false);
  const [movement, setMovement] = useState<'expense' | 'income'>(() => (transaction && transaction.amount_cents > 0 ? 'income' : 'expense'));
  const [date, setDate] = useState(transaction?.purchase_date ?? prefillDate ?? todayISO());
  const [description, setDescription] = useState(transaction?.description ?? '');
  const [amount, setAmount] = useState<number | null>(transaction ? Math.abs(transaction.amount_cents) : null);
  const [categoryId, setCategoryId] = useState<number | ''>(transaction?.category_id ?? '');
  const [count, setCount] = useState(1);
  const [notes, setNotes] = useState(transaction?.notes ?? '');
  const [originalAmount, setOriginalAmount] = useState<number | null>(transaction?.original_amount_cents ?? null);
  const [originalCurrency, setOriginalCurrency] = useState(transaction?.original_currency ?? '');

  const competence = useMemo(() => cardCompetence(card.closing_day, date), [card.closing_day, date]);

  const preview = useMemo(() => {
    if (!amount || !competence) return [];
    const n = isEdit ? 1 : count;
    if (n < 1 || n > MAX_INSTALLMENTS) return [];
    return splitInstallmentsCents(signed(movement, amount), n).map((cents, i) => ({ index: i + 1, label: competenceLabel(addCompetence(competence, i)), cents }));
  }, [amount, competence, count, isEdit, movement]);

  const currencyOk = originalCurrency === '' || /^[A-Za-z]{3}$/.test(originalCurrency);
  const canSubmit = !saving && !!date && !!description.trim() && !!amount && (isEdit || (count >= 1 && count <= MAX_INSTALLMENTS)) && currencyOk;

  const save = async () => {
    if (!canSubmit) return;
    const body = {
      credit_card_id: card.id,
      purchase_date: date,
      description: description.trim(),
      amount_cents: signed(movement, amount as number),
      category_id: categoryId === '' ? null : categoryId,
      notes: notes.trim() || null,
      original_amount_cents: originalAmount ?? null,
      original_currency: originalCurrency ? originalCurrency.toUpperCase() : null,
    };
    setSaving(true);
    try {
      if (isEdit && transaction) await creditCardService.updateTransaction(transaction.id, body);
      else await creditCardService.createTransaction({ ...body, installments: count });
      onSaved();
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="lg" closeOnBackdrop={!saving}>
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
        <Receipt size={18} className="text-slate-500" />
        <h2 className="text-h3">{isEdit ? 'Editar item da fatura' : 'Novo item na fatura'}</h2>
      </div>
      <div className="px-6 py-4 max-h-[70vh] overflow-y-auto custom-scrollbar grid grid-cols-1 sm:grid-cols-2 gap-3">
        {isInstallment && transaction && (
          <div className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 flex gap-2">
            <Layers size={16} className="flex-shrink-0 mt-0.5" />
            <span>
              Parcela <strong>{transaction.repeat_index}/{transaction.repeat_total}</strong>. A edição vale só para esta parcela.
            </span>
          </div>
        )}
        <div className="sm:col-span-2">
          <MovementToggle value={movement} onChange={setMovement} labels={{ expense: 'Compra', income: 'Estorno' }} />
        </div>
        <div>
          <label className={labelCls}>Cartão</label>
          <input className="input-field" value={card.name} disabled />
        </div>
        <div>
          <label className={labelCls}>
            Data da compra
            <Required />
          </label>
          <DateInputBR className="input-field w-full" value={date} onChange={setDate} />
          {competence && (
            <p className="text-small mt-1">
              Fatura <strong>{competenceLabel(competence)}</strong> (fechamento dia {card.closing_day})
            </p>
          )}
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>
            Descrição
            <Required />
          </label>
          <input className="input-field" maxLength={190} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Ex.: Supermercado" />
          {!isEdit && count > 1 && <p className="text-small mt-1">Cada parcela recebe o sufixo "i/N" (ex.: "Geladeira 1/10").</p>}
        </div>
        <div>
          <label className={labelCls}>
            {!isEdit && count > 1 ? 'Valor total' : 'Valor'}
            <Required />
          </label>
          <MoneyInput value={amount} onChange={setAmount} />
        </div>
        {!isEdit ? (
          <div>
            <label className={labelCls}>Nº de parcelas (1–{MAX_INSTALLMENTS})</label>
            <input type="number" className="input-field" min={1} max={MAX_INSTALLMENTS} value={count} onChange={(e) => setCount(Math.max(0, Math.trunc(Number(e.target.value) || 0)))} />
          </div>
        ) : (
          <div />
        )}
        <div>
          <label className={labelCls}>Categoria</label>
          <CategorySelect categories={categories} movement="expense" value={categoryId} onChange={setCategoryId} />
        </div>
        <div>
          <label className={labelCls}>Observações</label>
          <input className="input-field" maxLength={1000} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <div className="sm:col-span-2 grid grid-cols-[1fr_6rem] gap-2">
          <div>
            <label className={labelCls}>Valor na moeda original (opcional)</label>
            <MoneyInput value={originalAmount} onChange={setOriginalAmount} prefix={originalCurrency ? originalCurrency.toUpperCase() : '$'} />
          </div>
          <div>
            <label className={labelCls}>Moeda</label>
            <input
              className={clsx('input-field uppercase', !currencyOk && 'border-red-300')}
              maxLength={3}
              placeholder="USD"
              value={originalCurrency}
              onChange={(e) => setOriginalCurrency(e.target.value.replace(/[^A-Za-z]/g, '').slice(0, 3))}
            />
          </div>
        </div>
        {preview.length > 1 && (
          <div className="sm:col-span-2">
            <p className="text-label mb-1">Prévia das parcelas</p>
            <div className="rounded-lg border border-slate-200 max-h-48 overflow-y-auto custom-scrollbar">
              <table className="w-full text-sm">
                <tbody>
                  {preview.map((p) => (
                    <tr key={p.index} className="border-t first:border-t-0 border-slate-100">
                      <td className="px-3 py-1.5 text-slate-500 w-16">
                        {p.index}/{preview.length}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap">Fatura {p.label}</td>
                      <td className={clsx('px-3 py-1.5 text-right tabular-nums', p.cents < 0 ? 'text-red-600' : 'text-emerald-700')}>{formatBRL(p.cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-small mt-1">A sobra de centavos fica na 1ª parcela; cada parcela cai numa fatura consecutiva.</p>
          </div>
        )}
      </div>
      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={save} loading={saving} disabled={!canSubmit}>
          {isEdit ? 'Salvar' : count > 1 ? `Lançar ${count} parcelas` : 'Lançar'}
        </Button>
      </div>
    </Modal>
  );
};
